import { useMutation, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import type { Cargo, Departamento } from "@/componentes/cargos/cargo"
import { Modal } from "@/componentes/ui/Modal"

/**
 * Crear un departamento o corregir uno: un solo diálogo para los dos.
 *
 * ⚠ **Se monta de nuevo en cada apertura** —quien lo usa le cambia la `key`— y por eso
 * toma lo guardado en el `useState` inicial y no con un efecto. Con un efecto atado al
 * departamento, el refresco de la lista mientras está abierto —al volver a la ventana,
 * por ejemplo— devolvería los campos a lo guardado con lo tecleado dentro.
 *
 * ⚠ **El responsable es un CARGO, no una persona.** La estructura no cambia cuando
 * cambia quien ocupa el puesto: se elige el cargo y el sistema dice quién lo ocupa.
 */
export function DialogoDepartamento({
  abierto,
  departamento,
  cargos,
  areas,
  onCerrar,
}: {
  abierto: boolean
  /** `null` para crear uno nuevo. */
  departamento: Departamento | null
  /** El catálogo de cargos, para elegir el responsable (sólo se ofrecen los activos). */
  cargos: Cargo[]
  /** Las demás áreas, para elegir de cuál depende. */
  areas: Departamento[]
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [nombre, setNombre] = useState(departamento?.nombre ?? "")
  const [descripcion, setDescripcion] = useState(departamento?.descripcion ?? "")
  const [codigo, setCodigo] = useState(departamento?.codigo ?? "")
  const [siglas, setSiglas] = useState(departamento?.siglas ?? "")
  const [responsable, setResponsable] = useState(
    departamento?.cargo_responsable_id == null ? "" : String(departamento.cargo_responsable_id),
  )
  const [interino, setInterino] = useState(departamento?.responsable_interino ?? false)
  const [padre, setPadre] = useState(
    departamento?.padre_id == null ? "" : String(departamento.padre_id),
  )

  // Un cargo retirado no se ofrece, salvo el que ya es responsable: dejarlo fuera
  // haría que el selector enseñara «Sin responsable» sobre un área que sí lo tiene.
  const elegibles = cargos.filter((c) => c.activo || c.id === departamento?.cargo_responsable_id)
  // Ni ella misma ni las archivadas (salvo la que ya tiene).
  const posiblesPadres = areas.filter(
    (a) => a.id !== departamento?.id && (a.activo || a.id === departamento?.padre_id),
  )

  const guardar = useMutation({
    mutationFn: () => {
      // `null` y no cadena vacía: un dato borrado es que no lo tiene.
      const cuerpo = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || null,
        codigo: codigo.trim() || null,
        siglas: siglas.trim() || null,
        cargo_responsable_id: responsable === "" ? null : Number(responsable),
        responsable_interino: responsable !== "" && interino,
        padre_id: padre === "" ? null : Number(padre),
      }
      return departamento === null
        ? api.post<Departamento>("/api/v1/departamentos", cuerpo)
        : api.patch<Departamento>(`/api/v1/departamentos/${departamento.id}`, cuerpo)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["departamentos"] })
      // El nombre del área viaja dentro de cada cargo.
      clienteQuery.invalidateQueries({ queryKey: ["cargos"] })
      onCerrar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    guardar.mutate()
  }

  return (
    <Modal
      abierto={abierto}
      titulo={departamento === null ? "Nuevo departamento" : `Editar «${departamento.nombre}»`}
      descripcion={
        departamento === null
          ? "Nace sin cargos: cada cargo se coloca en su área al editarlo."
          : "El nombre nuevo sale en todos sus cargos y organigramas."
      }
      ancho="sm"
      onCerrar={onCerrar}
    >
      <form
        onSubmit={enviar}
        aria-label={departamento === null ? "Nuevo departamento" : "Editar departamento"}
      >
        <div className="grid grid-cols-[4.5rem_1fr] gap-3">
          <div>
            <label
              htmlFor="departamento-codigo"
              className="mb-1 block text-xs font-medium uppercase"
            >
              Código
            </label>
            <input
              id="departamento-codigo"
              inputMode="numeric"
              maxLength={2}
              className="campo tabular-nums"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="05"
            />
          </div>
          <div>
            <label
              htmlFor="departamento-nombre"
              className="mb-1 block text-xs font-medium uppercase"
            >
              Nombre
            </label>
            <input
              id="departamento-nombre"
              required
              minLength={2}
              maxLength={160}
              className="campo"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </div>
        </div>
        <label
          htmlFor="departamento-siglas"
          className="mt-3 mb-1 block text-xs font-medium uppercase"
        >
          Siglas
        </label>
        <input
          id="departamento-siglas"
          maxLength={6}
          className="campo"
          value={siglas}
          onChange={(e) => setSiglas(e.target.value.toUpperCase())}
          placeholder="FIN"
        />
        <label
          htmlFor="departamento-descripcion"
          className="mt-3 mb-1 block text-xs font-medium uppercase"
        >
          Para qué existe
        </label>
        <textarea
          id="departamento-descripcion"
          rows={3}
          className="campo"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Qué resuelve el área, en una o dos frases."
        />
        <label
          htmlFor="departamento-responsable"
          className="mt-3 mb-1 block text-xs font-medium uppercase"
        >
          Cargo responsable
        </label>
        <select
          id="departamento-responsable"
          className="campo"
          value={responsable}
          onChange={(e) => setResponsable(e.target.value)}
        >
          <option value="">Sin responsable</option>
          {elegibles.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nombre}
              {c.activo ? "" : " (retirado)"}
            </option>
          ))}
        </select>
        {responsable !== "" && (
          <label className="mt-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={interino}
              onChange={(e) => setInterino(e.target.checked)}
            />
            Lo lleva de forma interina, mientras se cubre el puesto propio del área
          </label>
        )}
        <label
          htmlFor="departamento-padre"
          className="mt-3 mb-1 block text-xs font-medium uppercase"
        >
          Depende de
        </label>
        <select
          id="departamento-padre"
          className="campo"
          value={padre}
          onChange={(e) => setPadre(e.target.value)}
        >
          <option value="">De la dirección</option>
          {posiblesPadres.map((a) => (
            <option key={a.id} value={a.id}>
              {a.codigo ? `${a.codigo} · ` : ""}
              {a.nombre}
            </option>
          ))}
        </select>
        {guardar.error && (
          <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
            {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className="boton-secundario h-9 px-3 text-sm">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={guardar.isPending}
            className="boton-primario h-9 px-3 text-sm"
          >
            {departamento === null ? "Crear departamento" : "Guardar"}
          </button>
        </div>
      </form>
    </Modal>
  )
}
